/**
 * Repository detail — repo metadata header + left sidebar section nav + main
 * panel. The header always loads the `overview` section (metadata + D1 review
 * stats); the main panel lazy-loads whichever section the sidebar selects.
 * All data comes from the Worker's GitHub-proxy endpoints.
 */
import { useState } from 'react';
import {
  ArrowLeft,
  Star,
  GitFork,
  GitBranch,
  CircleDot,
  GitPullRequest,
  AlertCircle,
  Users,
  Activity as ActivityIcon,
  ExternalLink,
  ShieldCheck,
  GitMerge,
  GitCommit,
} from 'lucide-react';
import {
  useProjectSection,
  type ProjectOverview,
  type RepoReviewStats,
  type ProjectBranch,
  type ProjectPull,
  type ProjectIssue,
  type ProjectContributor,
  type ProjectActivity,
  type ProjectSection,
} from '../lib/data';
import { Skeleton, SkeletonRepoHeader, SkeletonTable } from './Skeleton';

interface ProjectDetailProps {
  owner: string;
  repo: string;
  onBack: () => void;
}

const SECTIONS: { id: ProjectSection; label: string; icon: typeof CircleDot }[] = [
  { id: 'overview', label: 'Overview', icon: CircleDot },
  { id: 'branches', label: 'Branches', icon: GitBranch },
  { id: 'pulls', label: 'Pull Requests', icon: GitPullRequest },
  { id: 'issues', label: 'Issues', icon: AlertCircle },
  { id: 'contributors', label: 'Contributors', icon: Users },
  { id: 'activity', label: 'Activity', icon: ActivityIcon },
];

const concColor = (c: string | null | undefined): string => {
  if (c === 'approved') return 'var(--success, #2E7D32)';
  if (c === 'changes_requested') return 'var(--danger, #D32F2F)';
  return 'var(--warn, #ED6C02)';
};
const concLabel = (c: string | null | undefined): string => {
  if (c === 'approved') return 'Approved';
  if (c === 'changes_requested') return 'Changes';
  if (c === 'commented') return 'Commented';
  return '—';
};

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const day = Math.floor(ms / 86_400_000);
  if (day >= 30) return `${Math.floor(day / 30)}mo ago`;
  if (day >= 1) return `${day}d ago`;
  const hr = Math.floor(ms / 3_600_000);
  if (hr >= 1) return `${hr}h ago`;
  return `${Math.max(1, Math.floor(ms / 60_000))}m ago`;
}

const card: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  background: 'var(--bg)',
};

function State({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-secondary)' }}>{children}</div>;
}

/** Layout-matched loading placeholder for the right-hand panel.
 *  Mirrors the column rhythm of each section so the swap doesn't reflow. */
function panelSkeleton(section: ProjectSection): React.ReactNode {
  switch (section) {
    case 'overview':
      // Two-column key/value table.
      return (
        <div style={{ padding: 4 }} aria-busy="true" aria-label={`Loading ${section}`}>
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px', borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
              <span style={{ width: 180, flexShrink: 0 }}>
                <Skeleton width={100} height={10} rounded={3} />
              </span>
              <span style={{ flex: 1 }}>
                <Skeleton width={`${50 + (i % 3) * 18}%`} height={10} rounded={3} />
              </span>
            </div>
          ))}
        </div>
      );
    case 'branches':
      return <SkeletonTable rows={5} cols={['40%', '20%', '40%']} />;
    case 'pulls':
      return <SkeletonTable rows={5} cols={['46%', '14%', '20%', '10%', '10%']} />;
    case 'issues':
      return <SkeletonTable rows={5} cols={['46%', '14%', '24%', '16%']} />;
    case 'contributors':
      return <SkeletonTable rows={5} cols={['50%', '20%', '30%']} />;
    case 'activity':
      return (
        <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 16 }} aria-busy="true" aria-label="Loading activity">
          <Skeleton width={120} height={9} rounded={3} />
          <SkeletonTable rows={4} cols={['52%', '24%', '24%']} />
          <Skeleton width={120} height={9} rounded={3} />
          <SkeletonTable rows={4} cols={['14%', '54%', '20%', '12%']} />
        </div>
      );
  }
}

export function ProjectDetail({ owner, repo, onBack }: ProjectDetailProps) {
  const [section, setSection] = useState<ProjectSection>('overview');

  // Header always pulls overview (metadata + D1 review stats).
  const overviewQ = useProjectSection<ProjectOverview & { stats: RepoReviewStats }>(owner, repo, 'overview');
  // Main panel pulls the selected section — but skip the duplicate fetch when
  // the panel is showing overview (reuse the header's data instead).
  const sectionQ = useProjectSection<unknown>(owner, repo, section, { enabled: section !== 'overview' });

  const ov = overviewQ.data;
  const panelIsOverview = section === 'overview';
  const panelData = panelIsOverview ? overviewQ.data : sectionQ.data;
  const panelLoading = panelIsOverview ? overviewQ.loading : sectionQ.loading;
  const panelError = panelIsOverview ? overviewQ.error : sectionQ.error;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <button
        onClick={onBack}
        style={{
          alignSelf: 'flex-start',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--ink-secondary)',
          font: 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: 0,
        }}
        aria-label="Back to repositories"
      >
        <ArrowLeft size={16} /> Repositories
      </button>

      {/* Header */}
      <div style={{ ...card, padding: 16 }}>
        {overviewQ.loading && !ov ? (
          <SkeletonRepoHeader />
        ) : overviewQ.error ? (
          <State>
            <span style={{ color: 'var(--danger, #D32F2F)' }}>
              <AlertCircle size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
              {overviewQ.error}
            </span>
          </State>
        ) : ov ? (
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {ov.ownerAvatar ? (
              <img src={ov.ownerAvatar} alt="" width={40} height={40} style={{ borderRadius: 8 }} />
            ) : (
              <GitBranch size={32} style={{ color: 'var(--ink-secondary)' }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}>
                  {owner}/{repo}
                </h2>
                <a
                  href={ov.htmlUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{ color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                  aria-label="Open on GitHub"
                >
                  <ExternalLink size={14} />
                </a>
              </div>
              {ov.description ? (
                <p style={{ margin: '6px 0 0', color: 'var(--ink-secondary)', fontSize: 13 }}>{ov.description}</p>
              ) : null}
              <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap', fontSize: 13, color: 'var(--ink-secondary)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Star size={13} /> {ov.stars}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <GitFork size={13} /> {ov.forks}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <GitBranch size={13} /> {ov.defaultBranch}
                </span>
                {ov.language ? <span>{ov.language}</span> : null}
                {ov.license ? <span>{ov.license}</span> : null}
              </div>
            </div>
            {/* Review health (D1) */}
            <div style={{ display: 'flex', gap: 16, textAlign: 'right' }}>
              <Metric label="Reviews" value={String(ov.stats.reviewCount)} />
              <Metric label="Avg Score" value={ov.stats.reviewCount > 0 ? String(ov.stats.avgScore) : '—'} />
              <div>
                <div style={{ fontSize: 11, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Last Review
                </div>
                {ov.stats.lastConclusion ? (
                  <span
                    style={{
                      marginTop: 4,
                      display: 'inline-block',
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 'var(--radius)',
                      color: concColor(ov.stats.lastConclusion),
                      border: `1px solid ${concColor(ov.stats.lastConclusion)}`,
                    }}
                  >
                    {concLabel(ov.stats.lastConclusion)}
                    {ov.stats.lastScore != null ? ` · ${ov.stats.lastScore}` : ''}
                  </span>
                ) : (
                  <div style={{ color: 'var(--ink-secondary)' }}>—</div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Body: sidebar + main panel */}
      <div className="flex flex-col gap-4 md:flex-row" style={{ alignItems: 'flex-start', minWidth: 0, overflow: 'hidden' }}>
        <nav
          style={{
            width: 168,
            flexShrink: 0,
            ...card,
            padding: 6,
            position: 'sticky',
            top: 64,
          }}
          aria-label="Repository sections"
        >
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const active = section === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                aria-current={active ? 'page' : undefined}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  border: 'none',
                  borderRadius: 'calc(var(--radius) - 2px)',
                  background: active ? 'var(--accent-bg)' : 'transparent',
                  color: active ? 'var(--accent)' : 'var(--ink)',
                  cursor: 'pointer',
                  font: 'inherit',
                  fontSize: 13,
                  fontWeight: active ? 600 : 400,
                  textAlign: 'left',
                }}
              >
                <Icon size={14} /> {s.label}
              </button>
            );
          })}
        </nav>

        <div style={{ flex: 1, minWidth: 0, overflowX: 'auto', ...card }}>
          {panelLoading ? (
            panelSkeleton(section)
          ) : panelError ? (
            <State>
              <span style={{ color: 'var(--danger, #D32F2F)' }}>
                <AlertCircle size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
                {panelError}
              </span>
            </State>
          ) : (
            renderSection(section, panelData)
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', fontFamily: 'var(--font-mono)' }}>{value}</div>
    </div>
  );
}

// ---------- section renderers ----------
function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', minWidth: 620, borderCollapse: 'collapse', font: '13px var(--font-sans)' }}>
        <thead>
        <tr
          style={{
            borderBottom: '1px solid var(--border)',
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: 'var(--ink-secondary)',
          }}
        >
          {head.map((h) => (
            <th key={h} style={{ padding: '10px 12px', textAlign: 'left' }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
      </table>
    </div>
  );
}
const td: React.CSSProperties = { padding: '10px 12px', borderTop: '1px solid var(--border)', color: 'var(--ink)' };
const tdMute: React.CSSProperties = { ...td, color: 'var(--ink-secondary)' };
const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 12 };

function Empty({ label }: { label: string }) {
  return <State>{label}</State>;
}

function renderSection(section: ProjectSection, data: unknown): React.ReactNode {
  switch (section) {
    case 'overview':
      return <OverviewPanel data={data as (ProjectOverview & { stats: RepoReviewStats }) | null} />;
    case 'branches':
      return <BranchesPanel data={(data as ProjectBranch[] | null) ?? []} />;
    case 'pulls':
      return <PullsPanel data={(data as ProjectPull[] | null) ?? []} />;
    case 'issues':
      return <IssuesPanel data={(data as ProjectIssue[] | null) ?? []} />;
    case 'contributors':
      return <ContributorsPanel data={(data as ProjectContributor[] | null) ?? []} />;
    case 'activity': {
      // Normalize at the trust boundary: the cast is unvalidated `unknown`,
      // and a stale/differently-shaped KV cache hit can return 200 with a body
      // missing `merges`/`commits`. Default both halves so the panel can't throw.
      const a = (data ?? {}) as Partial<ProjectActivity>;
      return <ActivityPanel data={{ merges: a.merges ?? [], commits: a.commits ?? [] }} />;
    }
  }
}

function OverviewPanel({ data }: { data: (ProjectOverview & { stats: RepoReviewStats }) | null }) {
  if (!data) return <Empty label="No overview available." />;
  const rows: [string, string][] = [
    ['Description', data.description ?? '—'],
    ['Default branch', data.defaultBranch],
    ['Language', data.language ?? '—'],
    ['License', data.license ?? '—'],
    ['Stars', String(data.stars)],
    ['Forks', String(data.forks)],
    ['Open issues', String(data.openIssues)],
    ['Topics', data.topics.length ? data.topics.join(', ') : '—'],
    ['Updated', data.updatedAt ? timeAgo(data.updatedAt) : '—'],
    ['Reviews (ours)', String(data.stats.reviewCount)],
    ['Avg score (ours)', data.stats.reviewCount > 0 ? String(data.stats.avgScore) : '—'],
  ];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', font: '13px var(--font-sans)' }}>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <td style={{ ...tdMute, width: 180 }}>{k}</td>
            <td style={td}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BranchesPanel({ data }: { data: ProjectBranch[] }) {
  if (!data.length) return <Empty label="No branches." />;
  return (
    <Table head={['Branch', 'Protected', 'Last commit']}>
      {data.map((b) => (
        <tr key={b.name}>
          <td style={{ ...td, ...mono }}>
            <GitBranch size={12} style={{ verticalAlign: -2, marginRight: 6, opacity: 0.6 }} />
            {b.name}
          </td>
          <td style={tdMute}>
            {b.protected ? (
              <span style={{ color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <ShieldCheck size={12} /> yes
              </span>
            ) : (
              'no'
            )}
          </td>
          <td style={{ ...tdMute, ...mono }}>{b.sha ? b.sha.slice(0, 7) : '—'}</td>
        </tr>
      ))}
    </Table>
  );
}

function PullsPanel({ data }: { data: ProjectPull[] }) {
  if (!data.length) return <Empty label="No open pull requests." />;
  return (
    <Table head={['PR', 'Author', 'Branch', 'Updated', 'Our review']}>
      {data.map((p) => (
        <tr key={p.number}>
          <td style={td}>
            <a href={p.htmlUrl} target="_blank" rel="noreferrer noopener" style={{ color: 'var(--ink)', textDecoration: 'none' }}>
              <span style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)', marginRight: 6 }}>#{p.number}</span>
              {p.draft ? <span style={{ opacity: 0.5 }}>draft · </span> : null}
              {p.title}
            </a>
          </td>
          <td style={tdMute}>{p.author ?? '—'}</td>
          <td style={{ ...tdMute, ...mono }}>
            {p.head} → {p.base}
          </td>
          <td style={tdMute}>{timeAgo(p.updatedAt)}</td>
          <td style={td}>
            {p.reviewConclusion ? (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: 'var(--radius)',
                  color: concColor(p.reviewConclusion),
                  border: `1px solid ${concColor(p.reviewConclusion)}`,
                }}
              >
                {concLabel(p.reviewConclusion)}
                {p.reviewScore != null ? ` · ${p.reviewScore}` : ''}
              </span>
            ) : (
              <span style={{ color: 'var(--ink-secondary)' }}>not reviewed</span>
            )}
          </td>
        </tr>
      ))}
    </Table>
  );
}

function IssuesPanel({ data }: { data: ProjectIssue[] }) {
  if (!data.length) return <Empty label="No open issues." />;
  return (
    <Table head={['Issue', 'Author', 'Labels', 'Updated']}>
      {data.map((i) => (
        <tr key={i.number}>
          <td style={td}>
            <a href={i.htmlUrl} target="_blank" rel="noreferrer noopener" style={{ color: 'var(--ink)', textDecoration: 'none' }}>
              <span style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)', marginRight: 6 }}>#{i.number}</span>
              {i.title}
            </a>
          </td>
          <td style={tdMute}>{i.author ?? '—'}</td>
          <td style={td}>
            {(i.labels ?? []).length === 0
              ? '—'
              : (i.labels ?? []).map((l) => (
                  <span
                    key={l.name}
                    style={{
                      display: 'inline-block',
                      fontSize: 11,
                      padding: '1px 6px',
                      marginRight: 4,
                      borderRadius: 'var(--radius)',
                      border: '1px solid var(--border)',
                      color: `#${l.color}`,
                    }}
                  >
                    {l.name}
                  </span>
                ))}
          </td>
          <td style={tdMute}>{timeAgo(i.updatedAt)}</td>
        </tr>
      ))}
    </Table>
  );
}

function ContributorsPanel({ data }: { data: ProjectContributor[] }) {
  if (!data.length) return <Empty label="No contributors." />;
  const total = data.reduce((a, c) => a + c.contributions, 0) || 1;
  return (
    <Table head={['Contributor', 'Commits', 'Share']}>
      {data.map((c) => (
        <tr key={c.login}>
          <td style={td}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {c.avatar ? <img src={c.avatar} alt="" width={18} height={18} style={{ borderRadius: 4 }} /> : null}
              {c.login}
            </span>
          </td>
          <td style={{ ...td, ...mono }}>{c.contributions}</td>
          <td style={tdMute}>{Math.round((c.contributions / total) * 100)}%</td>
        </tr>
      ))}
    </Table>
  );
}

function ActivityPanel({ data }: { data: ProjectActivity }) {
  const hasAny = data.merges.length > 0 || data.commits.length > 0;
  if (!hasAny) return <Empty label="No recent activity." />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 4 }}>
      <div>
        <div
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: 'var(--ink-secondary)',
            padding: '10px 12px',
          }}
        >
          <GitMerge size={12} style={{ verticalAlign: -2, marginRight: 6 }} />
          Recent merges
        </div>
        {data.merges.length === 0 ? (
          <div style={{ ...tdMute, padding: '6px 12px' }}>None.</div>
        ) : (
          <Table head={['PR', 'Merged by', 'When']}>
            {data.merges.map((m) => (
              <tr key={m.number}>
                <td style={td}>
                  <a href={m.htmlUrl} target="_blank" rel="noreferrer noopener" style={{ color: 'var(--ink)', textDecoration: 'none' }}>
                    <span style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)', marginRight: 6 }}>#{m.number}</span>
                    {m.title}
                  </a>
                </td>
                <td style={tdMute}>{m.author ?? '—'}</td>
                <td style={tdMute}>{timeAgo(m.mergedAt)}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>
      <div>
        <div
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: 'var(--ink-secondary)',
            padding: '10px 12px',
          }}
        >
          <GitCommit size={12} style={{ verticalAlign: -2, marginRight: 6 }} />
          Recent commits
        </div>
        {data.commits.length === 0 ? (
          <div style={{ ...tdMute, padding: '6px 12px' }}>None.</div>
        ) : (
          <Table head={['Sha', 'Message', 'Author', 'When']}>
            {data.commits.map((c) => (
              <tr key={c.sha}>
                <td style={{ ...td, ...mono }}>
                  <a href={c.htmlUrl} target="_blank" rel="noreferrer noopener" style={{ color: 'var(--accent)' }}>
                    {c.sha.slice(0, 7)}
                  </a>
                </td>
                <td style={td}>{c.message.split('\n')[0]}</td>
                <td style={tdMute}>{c.author ?? '—'}</td>
                <td style={tdMute}>{timeAgo(c.date)}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </div>
  );
}
